import { inject, injectable } from "inversify";
import * as THREE from "three";
import { Line2 } from "three/examples/jsm/lines/Line2.js";
import type { IService } from "@designboticteam/core";
import { CursorService, DomElementsService, Logger, type PointerEventPayload, type PointerPosition } from "@designboticteam/core";
import { SNAPPABLE_LAYER } from "../../layers";
import { Workspace3DStoreService } from "../../services/Workspace3DStoreService";
import { CameraService } from "../../stage/CameraService";
import { SceneService } from "../../stage/SceneService";
import { isMeshOrLine, type MeshOrLine } from "../../utils/isMeshOrLine";
import { projectPointOntoSegment2D } from "../../utils/projectPointOntoSegment2D";
import { EDGE_PICK_PX, VERTEX_PICK_PX } from "../pickTolerance";
import { RaycastService } from "../RaycastService";

type ScreenVertex = {
	screen: { x: number; y: number };
	world: THREE.Vector3;
};

type SnapCandidate = {
	targetPosition: THREE.Vector3;
	distanceSq: number;
};

const CURSOR_OFFSET_PX = { x: -8, y: -8 };

/**
 * Snap detection works in 2D screen space (pixels), not 3D world space.
 * All vertices are projected onto the viewport before distance checks.
 * This ensures consistent snap thresholds regardless of camera distance —
 * 15px is always 15px whether the object is near or far.
 */
@injectable()
class CursorSnappingService implements IService {
	readonly serviceName = "👈 CursorSnappingService";
	private logger = new Logger(`🎬 WORKSPACE 3D | ${this.serviceName}`, {
		color: "#ffffff",
		backgroundColor: "#0066cc",
	});

	private worldPosition: THREE.Vector3 | null = null;
	private snapPosition: THREE.Vector3 | null = null;
	private screenPosition: PointerPosition | null = null;

	private snapExclusion: Array<{ sceneObject: THREE.Object3D; vertexIndices: Array<number> }> = [];

	// Override BufferGeometry's Float32 positions with Float64 originals to keep snap exact.
	private snapSources = new WeakMap<THREE.Object3D, () => ReadonlyArray<THREE.Vector3>>();

	private isContextActive = false;

	constructor(
		@inject(CursorService) private cursorDisplay: CursorService,
		@inject(DomElementsService) private domElementsService: DomElementsService,
		@inject(CameraService) private cameraService: CameraService,
		@inject(SceneService) private sceneService: SceneService,
		@inject(RaycastService) private raycastService: RaycastService,
		@inject(Workspace3DStoreService) private workspace3DStoreService: Workspace3DStoreService,
	) {}

	public initialize(): void {
		this.domElementsService.onPointerMove.subscribe(this.handlePointerMove);
		this.logger.log("initialized");
	}

	public dispose(): void {
		this.domElementsService.onPointerMove.unsubscribe(this.handlePointerMove);
		this.worldPosition = null;
		this.snapPosition = null;
		this.screenPosition = null;
		this.logger.log("disposed");
	}

	public pickPosition() {
		return {
			worldPosition: this.worldPosition,
			screenPosition: this.screenPosition,
		};
	}

	public pickSceneObject() {
		const { screenPosition } = this.pickPosition();

		if (screenPosition === null) {
			return null;
		}

		const hit = this.raycastService.pickObjectAt(screenPosition);
		const target = hit ? hit.object : null;

		return target;
	}

	public setSnapExclusion(snapExclusion: Array<{ sceneObject: THREE.Object3D; vertexIndices: Array<number> }>) {
		this.snapExclusion = snapExclusion;
	}

	public registerSnapSource(sceneObject: THREE.Object3D, getPoints: () => ReadonlyArray<THREE.Vector3>): void {
		this.snapSources.set(sceneObject, getPoints);
	}

	public unregisterSnapSource(sceneObject: THREE.Object3D): void {
		this.snapSources.delete(sceneObject);
	}

	/** User preference — snapping is allowed at all (Settings toggle). */
	public setSnappingEnabled(isSnappingEnabled: boolean): void {
		this.workspace3DStoreService.store.setState((state) => ({ cursor: { ...state.cursor, isSnappingEnabled } }));
	}

	/** Runtime gate — a snapping-capable (drawing) tool is active. Driven by ToolsManagerService. */
	public setContextActive(isContextActive: boolean): void {
		this.isContextActive = isContextActive;
	}

	private handlePointerMove = ({ relativePointerPosition }: PointerEventPayload): void => {
		if (this.cursorDisplay.isVisible === false) {
			return;
		}

		const targetPickPosition = {
			x: relativePointerPosition.x + CURSOR_OFFSET_PX.x,
			y: relativePointerPosition.y + CURSOR_OFFSET_PX.y,
		};

		const shouldSnap = this.workspace3DStoreService.store.getState().cursor.isSnappingEnabled && this.isContextActive;
		this.snapPosition = shouldSnap ? this.findSnapTarget(targetPickPosition) : null;

		if (this.snapPosition === null) {
			this.worldPosition = this.raycastService.pickGroundPointAt(targetPickPosition);
			this.screenPosition = { ...targetPickPosition };
			this.cursorDisplay.setScreenPosition(targetPickPosition.x, targetPickPosition.y);
		} else {
			const snapTargetPosition = this.getPositionOnViewport(this.snapPosition);

			if (snapTargetPosition) {
				this.worldPosition = this.snapPosition.clone();
				this.screenPosition = snapTargetPosition;
				this.cursorDisplay.setScreenPosition(snapTargetPosition.x, snapTargetPosition.y);
			} else {
				this.screenPosition = { ...targetPickPosition };
			}
		}
	};

	/**
	 * Traverses the scene and finds the closest snap target (vertex or edge) within threshold.
	 * */
	private findSnapTarget(pickPosition: PointerPosition): THREE.Vector3 | null {
		let closestVertex: SnapCandidate | null = null;
		let closestEdge: SnapCandidate | null = null;

		const candidates: MeshOrLine[] = [];
		this.sceneService.scene.traverse((sceneObject) => {
			if (isMeshOrLine(sceneObject) && sceneObject.layers.isEnabled(SNAPPABLE_LAYER)) {
				candidates.push(sceneObject);
			}
		});

		for (const child of candidates) {
			const screenVertices = this.collectScreenVertices(child);

			const vertex = this.findClosestVertex(pickPosition, screenVertices, closestVertex?.distanceSq ?? VERTEX_PICK_PX * VERTEX_PICK_PX);

			if (vertex !== null) {
				closestVertex = vertex;
			}

			const edge = this.findClosestEdge(pickPosition, child, screenVertices, closestEdge?.distanceSq ?? EDGE_PICK_PX * EDGE_PICK_PX);

			if (edge) {
				closestEdge = edge;
			}
		}

		return closestVertex?.targetPosition ?? closestEdge?.targetPosition ?? null;
	}

	/**
	 * Projects an object's confirmed vertices from 3D world space onto 2D viewport pixels. Skips vertices behind the camera.
	 */
	private collectScreenVertices(child: MeshOrLine): Array<ScreenVertex> {
		const exclusion = this.snapExclusion.find((x) => x.sceneObject === child);
		const excludedVertices = exclusion?.vertexIndices ?? [];

		const source = this.snapSources.get(child);
		const sourcePoints = source?.();

		// A Line2's `position` attribute is LineSegmentsGeometry's 8-vertex quad template, not the
		// polyline — the real points live in the instanced attributes. Reading it would snap to
		// garbage, so a Line2 only contributes when its owner registered a snap source (PolylineTool
		// always does).
		if (sourcePoints === undefined && child instanceof Line2) {
			return [];
		}

		const vertexCount = sourcePoints !== undefined ? sourcePoints.length : child.geometry.getAttribute("position").count;

		const vertices: ScreenVertex[] = [];

		child.updateMatrixWorld();

		for (let i = 0; i < vertexCount; i++) {
			if (excludedVertices.includes(i)) {
				continue;
			}

			let world: THREE.Vector3;

			if (sourcePoints !== undefined) {
				world = sourcePoints[i].clone();
			} else {
				world = new THREE.Vector3().fromBufferAttribute(child.geometry.getAttribute("position"), i);

				if (child instanceof THREE.Mesh) {
					world.applyMatrix4(child.matrixWorld);
				}
			}

			const screen = this.getPositionOnViewport(world);

			if (screen === null) {
				continue;
			}

			vertices.push({ screen, world });
		}

		return vertices;
	}

	/** Finds the vertex closest to cursor within the given pixel distance threshold. */
	private findClosestVertex(pickPosition: PointerPosition, vertices: ScreenVertex[], thresholdSq: number): SnapCandidate | null {
		let closest: SnapCandidate | null = null;

		for (const vertex of vertices) {
			const dx = pickPosition.x - vertex.screen.x;
			const dy = pickPosition.y - vertex.screen.y;
			const distanceSq = dx * dx + dy * dy;

			if (distanceSq < thresholdSq) {
				thresholdSq = distanceSq;
				closest = { targetPosition: vertex.world, distanceSq };
			}
		}

		return closest;
	}

	/** Finds the edge closest to cursor. Handles Line (sequential edges) and Mesh (triangle index edges). */
	private findClosestEdge(pickPosition: PointerPosition, child: MeshOrLine, vertices: ScreenVertex[], thresholdSq: number): SnapCandidate | null {
		let closest: SnapCandidate | null = null;

		/** Evaluates a single edge as a snap candidate. Updates closest/thresholdSq if this edge is nearer than the current best. */
		const evaluateEdgeSnapCandidate = (edgeStart: ScreenVertex, edgeEnd: ScreenVertex): void => {
			const closestPointOnEdge = projectPointOntoSegment2D({
				point: pickPosition,
				start: edgeStart.screen,
				end: edgeEnd.screen,
			});
			const dx = pickPosition.x - closestPointOnEdge.x;
			const dy = pickPosition.y - closestPointOnEdge.y;
			const distanceSq = dx * dx + dy * dy;

			if (distanceSq < thresholdSq) {
				thresholdSq = distanceSq;

				const edgeLength = Math.hypot(edgeEnd.screen.x - edgeStart.screen.x, edgeEnd.screen.y - edgeStart.screen.y);
				const projectionLength = Math.hypot(closestPointOnEdge.x - edgeStart.screen.x, closestPointOnEdge.y - edgeStart.screen.y);
				const t = edgeLength > 0 ? projectionLength / edgeLength : 0;

				closest = { targetPosition: edgeStart.world.clone().lerp(edgeEnd.world, t), distanceSq: distanceSq };
			}
		};

		// Line2 extends Mesh, so it would fall into the triangle branch below and walk the quad
		// template index of LineSegmentsGeometry instead of the polyline. Its edges are sequential,
		// like a THREE.Line's. LineSegments2 is deliberately not included — its points are pairs, and
		// nothing built from it is snappable anyway.
		if (child instanceof THREE.Line || child instanceof Line2) {
			for (let i = 0; i < vertices.length - 1; i++) {
				evaluateEdgeSnapCandidate(vertices[i], vertices[i + 1]);
			}
		} else if (child instanceof THREE.Mesh) {
			const index = child.geometry.getIndex();

			if (index) {
				const confirmedCount = child.userData.confirmedVertexCount ?? index.count;

				for (let i = 0; i < index.count; i += 3) {
					const idxA = index.getX(i);
					const idxB = index.getX(i + 1);
					const idxC = index.getX(i + 2);

					if (idxA >= confirmedCount || idxB >= confirmedCount || idxC >= confirmedCount) {
						continue;
					}

					const a = vertices[idxA];
					const b = vertices[idxB];
					const c = vertices[idxC];

					if (!a || !b || !c) {
						continue;
					}

					evaluateEdgeSnapCandidate(a, b);
					evaluateEdgeSnapCandidate(b, c);
					evaluateEdgeSnapCandidate(c, a);
				}
			}
		}

		return closest;
	}

	/**
	 * Projects a 3D world position to 2D viewport-local pixels. Returns null if behind the camera.
	 */
	public getPositionOnViewport(worldPosition: THREE.Vector3): { x: number; y: number } | null {
		const rect = this.domElementsService.containerElement.getBoundingClientRect();
		const camera = this.cameraService.camera;
		const projected = worldPosition.clone().project(camera);

		if (projected.z > 1) {
			return null;
		}

		return {
			x: ((projected.x + 1) / 2) * rect.width,
			y: ((-projected.y + 1) / 2) * rect.height,
		};
	}
}

export { CursorSnappingService };
